import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { taxPaymentsGateway } from '../index';
import { dashboardGateway } from '../../dashboard-v2/index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { ApiError } from '../../identity/types';
import { usePermissionCheck } from '../../identity/state/usePermissions';
import { useCurrentSubscription } from '../../subscription/useCurrentSubscription';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { listStateToParams, parseListState, toDashboardQuery, type ListState } from '../dashboardQuery';
import { DashboardFiltersBar } from '../components/DashboardFilters';
import { NO_FILTERS, TAB_PANEL_ID, filtersActive, tabId, type DashboardFilters } from '../filterModel';
import { DashboardPager } from '../components/DashboardPager';
import { PaymentDrawer } from '../drawer/PaymentDrawer';
import { PaymentQueue } from '../components/PaymentQueue';
import { PaymentSummary } from '../components/PaymentSummary';
import { PaymentsTable } from '../components/PaymentsTable';
import { StatusTabs } from '../components/StatusTabs';
import { SummaryCards } from '../components/SummaryCards';
import type { DashboardResponse, DashboardRow, DashboardTab } from '../dashboardTypes';
import { IdentityContext } from '../../identity/state/IdentityContext';
import { useTaxPaymentScope } from '../useTaxPaymentScope';
import '../tax-payment-dashboard.css';

type Failure = { kind: 'forbidden' } | { kind: 'error'; message: string };

const DEFAULT_NOTE = 'Simulation only. No tax payment is sent to any bank, payment provider or agency.';

/** The server's simulation note, with its first sentence in bold (it already begins "Simulation only."). */
function SimulationNote({ note }: { note: string | undefined }) {
  const text = note?.trim() || DEFAULT_NOTE;
  const end = text.indexOf('. ');
  return end < 0 ? <strong>{text}</strong> : <><strong>{text.slice(0, end + 1)}</strong> {text.slice(end + 2)}</>;
}

const NO_PAYMENTS_NOTE = 'Payments appear here once they are prepared from your tax liabilities. Nothing has been sent anywhere.';

/**
 * Tax Payment Dashboard (EPN-255; the EPN-48 simulated payments page, evolved in place at the same route). One table for the caller's own company, or, for a Super
 * Admin, every company through the platform door. Everything shown comes from the server's `dashboard` response: the status of each payment, the cards, the
 * tab counts and the queue. Nothing here sends a payment anywhere; this is the staging simulation. A company user is never sent to, and never asks for, another
 * company's payments. A figure the server cannot yet supply (Process No Later Than, Payroll Processor, Auto Tax Payment) is a dash, never an invented value.
 */
export function TaxPaymentDashboardPage() {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => parseListState(params), [params]);
  const scope = useTaxPaymentScope();
  const can = usePermissionCheck();
  const { subscription } = useCurrentSubscription();
  const platform = scope === 'platform';
  const me = useContext(IdentityContext)?.me ?? null;
  const ownCompanyName = me?.memberships.find((m) => m.companyId === me.activeCompanyId)?.companyName ?? null;
  const mayRead = platform || can('taxpayments.read');
  const readOnly = !platform && subscription !== null && subscription.accessMode !== 'Full';

  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [nonce, setNonce] = useState(0);
  const [summaryHidden, setSummaryHidden] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});

  const query = useMemo(() => toDashboardQuery(state), [state]);
  // The latest list state the user has asked for. The router applies a change to the address a moment after the click, so a second click made before it re-renders (a tab,
  // then a row) would otherwise rebuild the address from the old state and drop the first change. Every action reads and writes this, never the render-time `state`.
  const latest = useRef<ListState>(state);
  useEffect(() => { latest.current = parseListState(params); }, [params]);
  const update = useCallback((next: ListState, replace = true) => {
    latest.current = next;
    setParams(listStateToParams(next), { replace });
  }, [setParams]);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  // ---- the list: tab, filters, page and the cards/queue all come from one server response ---------------------------------------------------
  useEffect(() => {
    if (!mayRead) return undefined;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setFailure(null);
    taxPaymentsGateway.listDashboard(query, scope, controller.signal)
      .then((result) => { if (!controller.signal.aborted) setData(result); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.status === 403 && err.code === null) setFailure({ kind: 'forbidden' });
        else setFailure({ kind: 'error', message: friendlyErrorMessage(err, 'Could not load tax payments.') });
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [query, scope, mayRead, nonce]);

  // ---- Super Admin only: company names for the Company column (the payment rows carry only a company id) -----------------------------------
  useEffect(() => {
    if (!platform) return undefined;
    const controller = new AbortController();
    dashboardGateway.getSuperAdminSummary(controller.signal)
      .then((summary) => { if (!controller.signal.aborted) setCompanyNames(Object.fromEntries(summary.companies.map((c) => [c.id, c.name]))); })
      .catch(() => { /* the column then shows "Company"; the payments themselves still load */ });
    return () => controller.abort();
  }, [platform]);

  // ---- the open payment: the address names it; the drawer loads its own data (the row on this page is only its first paint) -------------------
  const selectedId = state.payment;
  const known = useMemo(() => {
    if (!selectedId || !data) return null;
    return data.items.find((r) => r.id === selectedId) ?? data.queue.find((r) => r.id === selectedId) ?? null;
  }, [selectedId, data]);

  // ---- actions ---------------------------------------------------------------------------------------------------------------------------
  const open = useCallback((row: DashboardRow) => update({ ...latest.current, payment: row.id }, false), [update]);
  const close = useCallback(() => update({ ...latest.current, payment: null }), [update]);

  const setTab = (tab: DashboardTab) => update({ ...latest.current, tab, page: 1, payment: null });
  const setFilters = (filters: DashboardFilters) => update({ ...latest.current, filters, page: 1 });
  const clearFilters = () => update({ ...latest.current, filters: NO_FILTERS, tab: 'All', page: 1 });

  const companyName = useCallback((companyId: string) => companyNames[companyId] ?? null, [companyNames]);
  const rows = data?.items ?? [];
  const filtered = filtersActive(state.filters);
  const failed = failure !== null;
  const firstLoad = loading && data === null;
  const emptyAll = !loading && !failed && data !== null && data.total === 0 && !filtered && state.tab === 'All';
  const emptyFiltered = !loading && !failed && data !== null && data.total === 0 && !emptyAll;

  return (
    <section className="tpd" aria-labelledby="tpd-title" aria-busy={loading} data-testid="tax-payment-dashboard">
      <div className="tpd-head">
        <h2 id="tpd-title" className="tpd-title">Tax Payment Dashboard</h2>
        {platform && <span className="tpd-scope" data-testid="platform-scope">All companies</span>}
      </div>

      <div className="tpd-banner" role="note" data-testid="simulation-banner">
        <SimulationNote note={data?.simulationNote} />
        {' '}Dates shown are the legal due date and the date you asked for; nothing here moves money.
      </div>

      {readOnly && (
        <div className="tpd-banner tpd-banner--info" role="note" data-testid="read-only-banner">
          <strong>Read only.</strong> Your plan lets you view tax payments, but changes such as recording an outside payment are not available until your subscription is active.
        </div>
      )}

      {!mayRead && <AccessDeniedNotice requiredPermissionLabel="Tax Payments (view)" />}

      {mayRead && (
        <>
          <SummaryCards summary={data?.summary ?? null} loading={firstLoad} />

          <div className={`tpd-panel${summaryHidden ? ' tpd-panel--wide' : ''}`}>
            {!summaryHidden && (
              <aside className="tpd-side" aria-label="Payment summary and queue">
                <PaymentSummary counts={data?.countsByTab ?? null} loading={firstLoad} />
                <PaymentQueue rows={data?.queue ?? []} loading={firstLoad} showCompany={platform} companyName={companyName} onOpen={open} />
              </aside>
            )}

            <div className="tpd-main">
              <DashboardFiltersBar
                filters={state.filters} onChange={setFilters} onClear={clearFilters} moreOpen={moreOpen} onToggleMore={() => setMoreOpen((v) => !v)}
                summaryHidden={summaryHidden} onToggleSummary={() => setSummaryHidden((v) => !v)} disabled={failed}
              />

              <StatusTabs tab={state.tab} counts={data?.countsByTab ?? null} onChange={setTab} />

              <div id={TAB_PANEL_ID} role="tabpanel" aria-labelledby={tabId(state.tab)} className="tpd-panel__body">
                {loading && <p className="tpd-muted" role="status">Loading tax payments…</p>}

                {failure?.kind === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="Tax Payments (view)" />}
                {failure?.kind === 'error' && (
                  <div className="tpd-error" role="alert">
                    <p>{failure.message}</p>
                    <button type="button" className="tpd-btn tpd-btn--outline" onClick={refresh}>Try again</button>
                  </div>
                )}

                {emptyAll && (
                  <div className="tpd-empty" data-testid="empty-state">
                    <p><strong>No tax payments yet.</strong></p>
                    <p className="tpd-muted">{NO_PAYMENTS_NOTE}</p>
                  </div>
                )}
                {emptyFiltered && (
                  <div className="tpd-empty" data-testid="empty-state">
                    <p><strong>No payments match this view.</strong></p>
                    <p className="tpd-muted">Clear the filters or pick another tab to see more.</p>
                    <button type="button" className="tpd-btn tpd-btn--outline" onClick={clearFilters}>Clear Filter</button>
                  </div>
                )}

                {!failed && rows.length > 0 && (
                  <>
                    <PaymentsTable rows={rows} showCompany={platform} companyName={companyName} selectedId={selectedId} onOpen={open} />
                    <DashboardPager
                      page={data?.page ?? 1} pageSize={data?.pageSize ?? state.pageSize} total={data?.total ?? 0} shown={rows.length}
                      onPage={(page) => update({ ...latest.current, page, payment: null })} onPageSize={(pageSize) => update({ ...latest.current, pageSize, page: 1, payment: null })}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {selectedId && (
        <PaymentDrawer
          key={selectedId} paymentId={selectedId} initialRow={known} scope={scope} readOnly={readOnly}
          companyName={platform ? (known ? companyName(known.companyId) : null) : ownCompanyName} onClose={close} onChanged={refresh}
        />
      )}
    </section>
  );
}
